import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useMatches,
} from "@tanstack/react-router";
import { useCallback } from "react";
import { establishmentQuery } from "@/api/establishment";
import { professionalsQuery } from "@/api/professionals";
import { useRealtimeSync } from "@/api/realtime";
import { warmQueries } from "@/api/warm";
import { useAuth } from "@/auth/AuthProvider";
import { pendingOnboarding } from "@/auth/onboarding";
import { BrandMark } from "@/components/brand-mark";
import { CommandPalette } from "@/components/command-palette";
import { FaixaDeSuporte } from "@/components/faixa-de-suporte";
import { InstalarApp } from "@/components/instalar-app";
import { navItems } from "@/components/nav-items";
import { NavUser } from "@/components/nav-user";
import { NotificationBell } from "@/components/notification-bell";
import { PageActionsHost, PageActionsProvider } from "@/components/page-actions";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authed")({
  // A sessão já foi resolvida no bootstrap do AuthProvider, então
  // `isAuthenticated` aqui é confiável e não precisa esperar nada.
  beforeLoad: ({ context, location }) => {
    if (!context.auth.isAuthenticated || !context.auth.user) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
    // Conta recém-criada só entra no painel depois de decidir a persona (ou
    // de dispensar a decisão) — o painel vazio não ajuda ninguém antes disso.
    if (pendingOnboarding()) throw redirect({ to: "/bem-vindo" });
    // Entra no contexto das rotas filhas pra que os `loader` possam aquecer
    // as queries. Sai daqui, e não de `useAuth`, porque loader não é
    // componente: não tem hook, e o id precisa estar resolvido ANTES de a
    // tela montar — que é o ponto inteiro do preload.
    return { establishmentId: context.auth.user.establishment_id };
  },
  // O estabelecimento e a lista de profissionais são pano de fundo de quase
  // toda tela: agenda, fila, serviços, profissionais e três grupos de
  // configuração. Buscá-los aqui, uma vez ao entrar no painel, tira os dois
  // do caminho crítico de cada tela depois — quando o Salão monta, metade do
  // que ele precisa já está no cache, e sobra o que só ele usa.
  loader: ({ context: { queryClient, establishmentId } }) =>
    warmQueries(queryClient, [
      establishmentQuery(establishmentId),
      professionalsQuery(establishmentId),
    ]),
  component: AuthedLayout,
});

function AuthedLayout() {
  // Uma conexão de tempo real por aba, aberta aqui e não em cada tela:
  // montada por rota, trocar de página derrubaria e reabriria o stream a
  // cada clique.
  useRealtimeSync();

  const auth = useAuth();
  const matches = useMatches();

  // Sair do suporte RECARREGA a aplicação, em vez de só trocar o estado.
  //
  // Não é preguiça: o cache do React Query está cheio de dado do
  // estabelecimento que acabou de ser deixado — agenda, clientes,
  // conversas. Continuar com ele em memória depois que a sessão mudou de
  // identidade mostraria dado de um tenant sob a sessão de outro, que é
  // exatamente o que este produto passa o dia inteiro impedindo.
  //
  // O reload também resolve o caso comum de quem opera o painel ter conta
  // própria aqui: o cookie de impersonação sai, o cookie pessoal continua
  // valendo, e a reidratação entra na sessão certa sozinha.
  const sairDoSuporte = useCallback(async () => {
    await auth.logout();
    window.location.assign("/");
  }, [auth]);
  const staticData = matches.at(-1)?.staticData;
  const title = staticData?.title ?? "Painel";
  const subtitle = staticData?.subtitle;

  return (
    // `h-screen h-dvh`: 100vh no celular é a altura da janela COM a barra do
    // navegador recolhida, então o painel media mais alto do que o espaço
    // realmente visível e a tab bar (que é `fixed` no rodapé) ficava embaixo
    // da barra de endereço até a pessoa rolar. `dvh` acompanha a barra; o
    // `h-screen` fica como fallback pra quem não suporta, e o Tailwind emite os
    // dois na ordem certa.
    <div className="flex h-screen h-dvh flex-col overflow-hidden">
      {/*
       * Sessão de suporte: a faixa fica por FORA do flex horizontal, no
       * topo de tudo, porque precisa ser vista antes de qualquer coisa —
       * inclusive antes de a pessoa começar a clicar. Em sessão normal
       * não renderiza nada.
       */}
      <FaixaDeSuporte onSair={sairDoSuporte} />

      <div className="flex flex-1 overflow-hidden">
        {/* Atalho global: vive no shell porque vale em qualquer tela do painel. */}
        <CommandPalette />
        {/* Sidebar (desktop) */}
        {/*
         * A sidebar é a MOLDURA: navy nos dois temas (o token --sidebar não
         * inverte). É o que faz o painel ler como aplicativo em vez de página —
         * e por isso todo texto aqui dentro usa os tokens `sidebar-*`, nunca os
         * do conteúdo: `text-muted-foreground` é cinza de fundo claro e sumiria
         * contra o navy.
         */}
        <aside className="hidden w-60 shrink-0 flex-col bg-sidebar text-sidebar-foreground lg:flex">
          <div className="flex h-16 items-center gap-2.5 px-5">
            {/* Sobre o navy o `a` do símbolo precisa virar branco, senão ele
              desaparece dentro da própria moldura. */}
            <BrandMark className="h-7 w-7 [--brand-mark-ink:#fff]" />
            <span className="text-lg font-semibold tracking-tight text-white">
              Agendo Certo
            </span>
          </div>
          <Separator className="bg-sidebar-border" />
          <nav className="flex-1 space-y-1 p-3">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                activeOptions={{ exact: item.exact }}
                activeProps={{ "data-active": true }}
                className={cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-sidebar-foreground/75 transition-colors",
                  "hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
                  "data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground",
                )}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </Link>
            ))}
          </nav>
          <Separator className="bg-sidebar-border" />
          <div className="p-4 text-xs text-sidebar-foreground/60">
            Plano Profissional
          </div>
        </aside>

        {/* Main column */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/*
           * Cabeçalho ÚNICO da tela: nome, subtítulo, ações da página e o canto
           * global (sino + conta). Antes eram duas barras empilhadas — esta,
           * com o nome, e outra logo abaixo desenhada por cada página, com o
           * MESMO nome mais o subtítulo.
           *
           * No CELULAR ele é uma linha só de 56px, e três coisas saem pra que
           * caiba: a marca (a tab bar de baixo já identifica o app — repetir o
           * símbolo aqui gastava 38px em cima de uma tela de 390), o subtítulo
           * (contexto útil, não essencial: quem está na tela já sabe onde
           * está) e o separador vertical. Com os três, o título e um botão de
           * ação não cabiam na mesma linha, o `flex-wrap` quebrava, e a barra
           * ia a 117px — 14% da altura da tela consumida antes do conteúdo,
           * somada aos 64px da tab bar.
           *
           * `sm:` pra cima nada disso vale: a marca some só no `lg` (onde a
           * sidebar assume), o subtítulo volta e a barra cresce de novo.
           */}
          <PageActionsProvider>
            <header className="flex h-14 shrink-0 items-center justify-between gap-2 border-b bg-card px-4 sm:h-auto sm:min-h-16 sm:flex-wrap sm:gap-x-4 sm:gap-y-2 sm:px-6 sm:py-3">
              <div className="flex min-w-0 flex-1 items-center gap-2.5 sm:flex-none">
                <BrandMark className="hidden h-7 w-7 shrink-0 sm:block lg:hidden" />
                <div className="min-w-0">
                  <h1 className="truncate text-base font-semibold">{title}</h1>
                  {subtitle && (
                    <p className="hidden truncate text-xs text-muted-foreground sm:block">
                      {subtitle}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1 sm:gap-3">
                {/* As ações da página entram aqui, por portal (page-actions.tsx). */}
                <PageActionsHost />
                <NotificationBell />
                <Separator orientation="vertical" className="hidden h-8 sm:block" />
                <NavUser />
              </div>
            </header>

            {/* Page content */}
            <main className="min-h-0 flex-1 overflow-auto pb-16 lg:pb-0">
              <Outlet />
            </main>
          </PageActionsProvider>
        </div>
      </div>

      {/* Convite de instalação: fica ACIMA da tab bar, e só no celular de quem
          ainda não instalou. Mora aqui, e não numa tela, porque não é assunto
          de nenhuma delas — e porque só faz sentido pra quem já entrou. */}
      <InstalarApp />

      {/* Bottom tab bar (mobile) */}
      {/* Mesma moldura da sidebar: no celular a tab bar É o chrome. */}
      <nav className="fixed inset-x-0 bottom-0 z-20 flex h-16 items-stretch bg-sidebar text-sidebar-foreground lg:hidden">
        {navItems
          .filter((item) => item.mobile)
          .map((item) => (
            <Link
              key={item.to}
              to={item.to}
              activeOptions={{ exact: item.exact }}
              activeProps={{ "data-active": true }}
              className={cn(
                "flex flex-1 flex-col items-center justify-center gap-1 text-[10px] font-medium text-sidebar-foreground/70",
                "data-[active=true]:text-sidebar-primary",
              )}
            >
              <item.icon className="h-5 w-5" />
              {"shortLabel" in item ? item.shortLabel : item.label}
            </Link>
          ))}
      </nav>
    </div>
  );
}
